import React, { useRef, useMemo } from 'react';
import { useTable, useRowSelect, useFilters, useSortBy, useFlexLayout } from 'react-table';
import { array, bool, number, func, element } from 'prop-types';
import { Box, Flex } from 'rebass';
import { CheckboxStyled, TextStyled, ButtonStyled, useTokens } from '@nike/nike-design-system-components';

import { filterYesNoFromBoolean } from './filterFunctions';
import useWindowSize from '../../hooks/useWindowSize';
import useModal from '../../hooks/useModal';
import { scrollbarWidth } from '../tableServerSide/helpers';

import { TableStyled } from './styledTableElements';
import { ActionItemList } from '../../admin/common/ActionItemList';
import Spinner from '../spinner/Spinner';
import { Icon } from '../index';
import { StickyButton } from '../tableServerSide/styles';

/*
 * React table with sorting and filtering capabilties.
 * All table data is fetched initially in parent component and passed to TableClientSide.
 */
const TableClientSide = ({
  columns,
  data, // All fetched table data, any updates to it are made in parent component and passed
  isLoading, // The only prop whose value change will re-render this component
  includeRowSelect, // Whether to include a column for selecting rows
  fullHeight,
  fullWidth,
  height,
  rightHeaderSlot, // Optional element or component rendering on upper right above table
  getActionItems, // If actions required, function that passes params {closeModal, selectedFlatRows} to get action items
  noBorders
}) => {
  const tokens = useTokens();

  const [isModalOpen, openModal, closeModal] = useModal();

  // Custom filter functions
  const filterTypes = useMemo(
    () => ({
      yesNo: filterYesNoFromBoolean
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    selectedFlatRows,
    setSortBy,
    setAllFilters
  } = useTable({ columns, data, filterTypes }, useFlexLayout, useFilters, useSortBy, useRowSelect, hooks => {
    if (!includeRowSelect) return;

    hooks.visibleColumns.push(columns => [
      {
        width: 30,
        id: 'selection',
        // eslint-disable-next-line react/prop-types
        Header: ({ getToggleAllRowsSelectedProps }) => (
          <CheckboxStyled {...getToggleAllRowsSelectedProps()} indeterminate='false' />
        ),
        // eslint-disable-next-line react/prop-types
        Cell: ({ row }) => <CheckboxStyled {...row.getToggleRowSelectedProps()} indeterminate='false' />
      },
      ...columns
    ]);
  });

  const tableRef = useRef(null);
  const tableBodyRef = useRef(null);

  const { width } = useWindowSize();
  const scrollbarSize = useMemo(scrollbarWidth, [width]);
  const heightToBottomOfWindow = window.innerHeight - (tableRef?.current?.offsetTop + 55 + scrollbarSize);

  const scrollToTop = () => tableBodyRef.current?.scrollTo?.({ left: 0, top: 0, behavior: 'auto' });

  const memoizedActionItems = getActionItems
    ? useMemo(() => getActionItems({ closeModal, selectedFlatRows }), [closeModal, selectedFlatRows])
    : null;

  const handleClearSortAndFilters = () => {
    setSortBy([]);
    setAllFilters([]);
  };

  return (
    <>
      <Flex p='12px' width='100%'>
        <Flex alignItems='center' width='50%'>
          <TextStyled appearance='body2' style={{ marginLeft: '5px' }}>
            {rows.length} results
          </TextStyled>

          <ButtonStyled
            appearance='text'
            background='light'
            ariaLabel='Clear All Filters'
            type='button'
            onClick={handleClearSortAndFilters}
            style={{ margin: '0 10px' }}
          >
            Clear All Filters
          </ButtonStyled>

          {memoizedActionItems && (
            <ActionItemList
              selected={selectedFlatRows}
              actionItems={memoizedActionItems}
              isModalOpen={isModalOpen}
              openModal={openModal}
              closeModal={closeModal}
            />
          )}
        </Flex>

        <Flex justifyContent='flex-end' width='50%'>
          {rightHeaderSlot}
        </Flex>
      </Flex>
      <Box px='12px'>
        <TableStyled
          {...getTableProps()}
          ref={tableRef}
          designTokens={tokens}
          fullWidth={fullWidth}
          scrollbarSize={scrollbarSize}
          noBorders={noBorders}
        >
          <div>
            {headerGroups.map(headerGroup => (
              <div {...headerGroup.getHeaderGroupProps()} className='tr'>
                {headerGroup.headers.map(column => (
                  <div {...column.getHeaderProps()} className='th'>
                    {column.render('Header')}
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div
            {...getTableBodyProps()}
            className='tbody'
            ref={tableBodyRef}
            style={{ height: `${fullHeight ? heightToBottomOfWindow : height}px` }}
          >
            {
              // eslint-disable-next-line no-nested-ternary
              isLoading ? (
                <Spinner large />
              ) : rows.length === 0 ? (
                <Box p='15px' style={{ position: 'absolute' }}>
                  <TextStyled appearance='body2'>No Data Available.</TextStyled>
                </Box>
              ) : (
                rows.map(row => {
                  prepareRow(row);
                  return (
                    <div {...row.getRowProps()} className='tr'>
                      {row.cells.map(cell => (
                        <div {...cell.getCellProps()} className='td'>
                          {cell.render('Cell')}
                        </div>
                      ))}
                    </div>
                  );
                })
              )
            }
          </div>
        </TableStyled>
      </Box>

      <StickyButton type='button' onClick={scrollToTop}>
        <Icon name='arrow-up' isLight alt='Arrow Up Icon' />
      </StickyButton>
    </>
  );
};

TableClientSide.propTypes = {
  columns: array.isRequired,
  data: array,
  isLoading: bool.isRequired,
  includeRowSelect: bool,
  fullHeight: bool,
  fullWidth: bool,
  height: number,
  rightHeaderSlot: element,
  getActionItems: func,
  noBorders: bool
};

TableClientSide.defaultProps = {
  data: [],
  includeRowSelect: false,
  fullHeight: false,
  fullWidth: false,
  height: 500,
  rightHeaderSlot: null,
  getActionItems: null,
  noBorders: false
};

export default React.memo(TableClientSide, (prevProps, nextProps) => prevProps.isLoading === nextProps.isLoading);
